import type { RecoveryDecision } from './crashRecovery';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';

const REVERT_REPORT_KEY = 'baobab_revert_report';

export type RevertReason = Extract<RecoveryDecision, { kind: 'revert' }>['reason'];

export interface RevertReport {
  reason: RevertReason;
  deniedVersion: string;
}

export const persistRevertReport = (
  reason: RevertReason,
  deniedVersion: string,
): void => {
  try {
    localStorage.setItem(
      REVERT_REPORT_KEY,
      JSON.stringify({ reason, deniedVersion }),
    );
  } catch {
    // Best-effort only — losing this report must never block the revert it describes.
  }
};

export const readRevertReport = (): RevertReport | null => {
  try {
    const raw = localStorage.getItem(REVERT_REPORT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RevertReport>;
    if (
      typeof parsed?.reason !== 'string' ||
      typeof parsed?.deniedVersion !== 'string'
    ) {
      return null;
    }
    return {
      reason: parsed.reason as RevertReason,
      deniedVersion: parsed.deniedVersion,
    };
  } catch {
    // Corrupt or unavailable storage must never block boot.
    return null;
  }
};

export const clearRevertReport = (): void => {
  try {
    localStorage.removeItem(REVERT_REPORT_KEY);
  } catch {
    // Best-effort only.
  }
};

export const emitPendingRevertReport = (): void => {
  const report = readRevertReport();
  if (!report) return;
  logAnalyticsEvent('app_bundle_recovered', {
    failureReason: report.reason,
    fromVersion: report.deniedVersion,
  });
  clearRevertReport();
};
